import type { SpecRangeDto } from "./product-line";
import type { TenantAgreementStatusDto } from "@/services/admin-tenant.service";

export type AdminRole = "SuperAdmin" | "BillingAdmin" | "SupportAdmin";

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  roles: AdminRole[];
  permissions: string[];
  createdAt: string;
  lastLoginAt: string;
}

export interface AuthTokens {
  token: string;
  refreshToken: string;
  expiresIn: number;
}

// ============================================================================
// Product Management Types
// ============================================================================

/**
 * Product type for UI display (simplified from ProductListDto)
 */
export interface Product {
  id: string;
  name: string;
  description?: string;
  sku: string;
  category: string;
  basePrice?: number;
  currency: string;
  status: "Active" | "Inactive";
  createdAt: string;
}

export type ProductCategory =
  | "VM"
  | "Storage"
  | "Network"
  | "Database"
  | "Other";
export type ProductStatus = "Active" | "Inactive";

/**
 * Resource type enumeration (matches backend ResourceType enum)
 *
 * Backend enum order: VirtualMachine=0, Storage=1, Database=2, Network=3,
 * LoadBalancer=4, ObjectStorage=5, Domain=6.
 * `Domain` is an OrderItem line-type discriminator only — domains are never
 * Resource rows (see docs/design/2026-05-22-resourcetype-domain-impact-audit.md).
 */
export type ResourceType =
  | "VirtualMachine"
  | "Storage"
  | "Database"
  | "Network"
  | "LoadBalancer"
  | "ObjectStorage"
  | "Domain";

/**
 * Pricing type enumeration (matches backend PricingType enum)
 */
export type PricingType = "Fixed" | "ComponentBased" | "PromoCode";

/**
 * Calculation method enumeration (matches backend CalculationMethod enum)
 */
export type CalculationMethod = "Sum" | "SumOfProducts" | "Formula";

/**
 * Billing frequency enumeration (matches backend BillingFrequency enum)
 */
export type BillingFrequency = "Hourly" | "Daily" | "Monthly" | "Yearly";

/**
 * Product line summary embedded in product detail responses
 */
export interface ProductLineSummaryDto {
  id: string;
  name: string;
  tier: string;
  slug: string;
}

/**
 * Product list item DTO (from ProductListDto)
 *
 * Represents a product in list view with basic information and pricing.
 * - BasePrice is in smallest currency unit (e.g., 1099 = $10.99 for USD)
 * - HasCustomPricing indicates if product has non-standard pricing
 */
export interface ProductListDto {
  /** Product ID (UUID) */
  id: string;

  /** Product name */
  name: string;

  /** Product slug (URL-friendly identifier) */
  slug: string;

  /** Category name */
  category: string;

  /** Resource type (VirtualMachine, Storage, Network, Database) */
  resourceType: ResourceType;

  /** Base price in smallest currency unit (null if no pricing set) */
  basePrice?: number; // Smallest currency unit (renamed from basePriceCents)

  /** Currency code (e.g., "VND", "USD") */
  currency: string;

  /** Whether product is active */
  isActive: boolean;

  /** Whether product is configurable */
  isConfigurable: boolean;

  /** Sort order for display */
  sortOrder: number;

  /** Creation timestamp (ISO 8601) */
  createdAt: string;
}

/**
 * Product detail DTO (from ProductDetailDto)
 *
 * Complete product information with pricing rules.
 */
export interface ProductDetailDto {
  /** Product ID (UUID) */
  id: string;

  /** Product name */
  name: string;

  /** Product slug (URL-friendly identifier) */
  slug: string;

  /** Product description */
  description?: string;

  /** Category name */
  category: string;

  /** Resource type (VirtualMachine, Storage, Network, Database) */
  resourceType: ResourceType;

  /** Whether product is active */
  isActive: boolean;

  /** Whether product is configurable */
  isConfigurable: boolean;

  /** Configuration schema JSON */
  configurationSchema: string;

  /** Icon URL */
  iconUrl?: string;

  /** Sort order for display */
  sortOrder: number;

  /** Metadata JSON */
  metadata: string;

  /** Product lines this product belongs to */
  productLines: ProductLineSummaryDto[];

  /** Associated pricing rules */
  pricingRules: PricingRuleDto[];

  /** Creation timestamp (ISO 8601) */
  createdAt: string;

  /** Last update timestamp (ISO 8601) */
  updatedAt?: string;
}

/**
 * Pricing rule DTO (from PricingRuleDto)
 *
 * Represents a pricing rule for a product.
 * - BasePrice is in dollars (converted from cents for display)
 * - PriceDisplay is formatted for UI (e.g., "$10.99/month")
 * - IsCurrentlyEffective indicates if rule applies now (effectiveFrom <= now < effectiveTo)
 */
export interface PricingRuleDto {
  /** Pricing rule ID (UUID) */
  id: string;

  /** Product ID (UUID) */
  productId: string;

  /** Pricing type (Fixed, ComponentBased, PromoCode) */
  pricingType: string;

  /** Base price in dollars (null for component-based or formula) */
  basePrice?: number;

  /** Pricing formula (null for fixed pricing) */
  formula?: string;

  /** Calculation method (Sum, SumOfProducts, Formula) */
  calculationMethod: string;

  /** Billing frequency (Hourly, Daily, Monthly, Yearly) */
  billingFrequency: string;

  /** Region code (null = global) */
  region?: string;

  /** Currency code (e.g., "USD") */
  currency: string;

  /** Whether pricing rule is active */
  isActive: boolean;

  /** Effective from date (ISO 8601) */
  effectiveFrom: string;

  /** Effective to date (ISO 8601, null = no end date) */
  effectiveTo?: string;

  /** Creation timestamp (ISO 8601) */
  createdAt: string;

  /** Formatted price display (e.g., "$10.99/month") */
  priceDisplay?: string;

  /** Whether this rule is currently effective */
  isCurrentlyEffective: boolean;
}

/**
 * Create product request DTO (from CreateProductDto)
 */
export interface CreateProductRequest {
  /** Product name */
  name: string;

  /** Product slug (URL-friendly identifier) */
  slug: string;

  /** Product description */
  description: string;

  /** Resource type */
  resourceType: ResourceType;

  /** Category name */
  category: string;

  /** Whether product is configurable */
  isConfigurable: boolean;

  /** Configuration schema JSON (optional) */
  configurationSchema?: string;

  /** Icon URL (optional) */
  iconUrl?: string;

  /** Sort order (optional, default: 0) */
  sortOrder?: number;

  /** Metadata JSON (optional) */
  metadata?: string;

  /** Product line IDs (optional) */
  productLineIds?: string[];
}

/**
 * Update product request DTO (from UpdateProductDto)
 */
export interface UpdateProductRequest {
  /** Product name (optional) */
  name?: string;

  /** Product description (optional) */
  description?: string;

  /** Whether product is active (optional) */
  isActive?: boolean;

  /** Icon URL (optional) */
  iconUrl?: string;

  /** Sort order (optional) */
  sortOrder?: number;

  /** Metadata JSON (optional) */
  metadata?: string;

  /** Product line IDs (optional, empty array clears all assignments) */
  productLineIds?: string[];
}

/**
 * Create pricing rule request DTO (from CreatePricingRuleDto)
 */
export interface CreatePricingRuleRequest {
  /** Pricing type */
  pricingType: PricingType;

  /** Base price in smallest currency unit (required for Fixed pricing) */
  basePrice?: number; // Smallest currency unit (renamed from basePriceCents)

  /** Pricing formula (for Formula calculation) */
  formula?: string;

  /** Calculation method */
  calculationMethod: CalculationMethod;

  /** Billing frequency */
  billingFrequency: BillingFrequency;

  /** Region code (null = global) */
  region?: string;

  /** Currency code (default: "USD") */
  currency?: string;

  /** Effective from date (optional, defaults to now) */
  effectiveFrom?: string;

  /** Effective to date (optional) */
  effectiveTo?: string;
}

/**
 * Update pricing rule request DTO (from UpdatePricingRuleDto)
 */
export interface UpdatePricingRuleRequest {
  /** Base price in smallest currency unit (optional) */
  basePrice?: number; // Smallest currency unit (renamed from basePriceCents)

  /** Pricing formula (optional) */
  formula?: string;

  /** Whether pricing rule is active (optional) */
  isActive?: boolean;

  /** Effective to date (optional) */
  effectiveTo?: string;
}

// ============================================================================
// Resource Monitoring Types
// ============================================================================

/**
 * Resource status values, mirroring the backend `ResourceStatus` enum exactly.
 *
 * These are the same strings the API returns AND accepts as the `status` filter, so never
 * introduce display-only aliases here — `Starting`/`Stopping` used to exist and made the filter
 * return 400 while hiding `Terminating`/`Terminated` behind them.
 */
export const RESOURCE_STATUSES = [
  "Provisioning",
  "Running",
  "Stopped",
  "Suspended",
  "Error",
  "Terminating",
  "Terminated",
  "Pending",

  // Set by the reinstall flow, and the one member this list used to be missing — the admin
  // "Change status" action can write it, so a row holding it must be renderable.
  "Decommissioning",
] as const;

export type ResourceStatus = (typeof RESOURCE_STATUSES)[number];

/**
 * How long a deleted VM with a real machine at the provider is kept before it is purged, counted from
 * the moment n8n deleted it — mirrors `Resource.RestoreWindow` on the backend.
 */
export const RESTORE_WINDOW_HOURS = 1;

/**
 * A resource's current lifecycle state from `GET /api/v1/admin/resources/statuses` — mirrors the
 * backend `ResourceStatusDto`. Read for rows whose status changes in the background (a deletion
 * finishing). Datetimes are UTC ISO, possibly without a trailing `Z`.
 */
export interface ResourceStatusSnapshot {
  id: string;
  name: string;
  type: ResourceType;
  status: ResourceStatus;
  /** When the resource was deleted; null while it is live. */
  deletedAt: string | null;
  /** Until when a Decommissioning (deleted) VM is kept before it is purged; null otherwise. */
  restorableUntil: string | null;
  /** When the VM's last deletion failed for good and it was put back; null when none has. */
  deletionFailedAt: string | null;
  updatedAt: string;
}

/** One row moved by a status correction — mirrors the backend `ResourceStatusChangeDto`. */
export interface ResourceStatusChange {
  resourceId: string;
  resourceName: string;
  resourceType: string;
  previousStatus: ResourceStatus;
}

/**
 * What `PUT /api/v1/admin/resources/{id}/status` actually wrote — mirrors
 * `ResourceStatusUpdateDto`. `changes` lists only the rows that moved, so an empty array means
 * every row was already at the requested status and nothing was written.
 */
export interface ResourceStatusUpdate {
  status: ResourceStatus;
  changes: ResourceStatusChange[];
}

/** One row written by a public-IP correction — mirrors `ResourcePublicIpChangeDto`. */
export interface ResourcePublicIpChange {
  resourceId: string;
  resourceName: string;
  resourceType: string;

  /** What the row held before, verbatim — CIDR suffix included when it carried one. */
  previousValue: string | null;

  /**
   * The fields written on this row, named as they are stored (`ProviderIpAddress`,
   * `metadata.PublicIpAddress`, `metadata.network.net0.ip`, `metadata.IpAddress`, …). One address
   * lives in several places behind a single VM UUID, and this is the record of which were touched.
   */
  fields: string[];
}

/**
 * What `PUT /api/v1/admin/resources/{id}/public-ip` actually wrote — mirrors
 * `ResourcePublicIpUpdateDto`. `changes` lists only the rows that moved, so an empty array means
 * every row already held the address and nothing was written.
 */
export interface ResourcePublicIpUpdate {
  resourceId: string;
  vmUuid: string | null;

  /** The address on record before, without any CIDR suffix. Null when nothing was recorded. */
  previousIpAddress: string | null;

  /** The new address without a CIDR suffix — what the searchable column now holds. */
  ipAddress: string;

  /** The new address as typed, CIDR suffix included — what the metadata documents now hold. */
  storedValue: string;

  changes: ResourcePublicIpChange[];

  /**
   * True when the status sync still revisits this VM (Running or Stopped, not suspended,
   * WiService-backed). That scan runs every five minutes and writes whatever address the provider
   * reports, so the correction holds only until the provider disagrees.
   */
  willBeOverwrittenBySync: boolean;
}

/** What the operator asked the machine to be reconfigured to. */
export interface ApplyVmIpAddressPayload {
  /** New address. A CIDR suffix is optional — without one the machine's current prefix is reused. */
  ipAddress: string;

  /** Gateway, without a suffix. Omitted carries the machine's current one over. */
  gateway?: string;

  /** Interface to reconfigure, by MAC. Only needed to disambiguate a multi-homed machine. */
  macAddress?: string;

  /** Whether to generate a new OS login in the same operation — one restart serves both. */
  resetPassword: boolean;

  reason?: string;
}

/**
 * What `POST /api/v1/admin/resources/{id}/apply-ip-address` actually did — mirrors
 * `ApplyVmIpAddressDto`. The address is written into the machine's cloud-init configuration; it is
 * not live until the VM is restarted, which nothing in this call does.
 */
export interface ApplyVmIpAddressResult {
  resourceId: string;
  vmUuid: string;

  /** MAC of the interface that was reconfigured — cloud-init addresses interfaces by MAC. */
  macAddress: string;

  /** What the machine's cloud-init held before, as `ip/prefix`. Null when it reported none. */
  previousIpAddress: string | null;

  /** What it holds now, as `ip/prefix`. */
  ipAddress: string;
  gateway: string;

  /** The provider's workflow execution id, for tracing in n8n. Zero when nothing was sent. */
  executionId: number;

  /** The machine already held exactly this configuration, so nothing was sent. It still needs the restart. */
  alreadyConfigured: boolean;

  /** Always true: cloud-init is read at boot, and this call restarts nothing. */
  requiresReboot: boolean;

  /** WiCloud rows brought into line once the provider accepted. */
  recordChanges: ResourcePublicIpChange[];

  /**
   * Why WiCloud's record was not updated although the machine was. Null when it was, or already
   * matched. Reported rather than swallowed, so "configured" never sits over a stale record silently.
   */
  recordUpdateError: string | null;

  /**
   * The status sync reads a VM's address from the guest agent — the running OS, not the cloud-init
   * config just written — so on a Running VM the record shows the old address again until the restart.
   */
  recordRevertsUntilRestart: boolean;

  /** Set only when a password reset was asked for and succeeded. Readable exactly once. */
  osUsername: string | null;
  newPassword: string | null;

  /** Why the password reset did not happen. The address change still did — that is the point of reporting it. */
  passwordResetError: string | null;
}

/**
 * The fields both resource endpoints return. Split out because the list and the detail endpoints
 * are backed by different DTOs and do NOT return the same shape: `ResourceDetail` used to extend
 * `Resource`, which declared `lastActivityAt`, `userEmail` and friends as always-present. The
 * detail endpoint never sends them, so the types lied, `tsc` could not see it, and the detail page
 * crashed on `formatDate(undefined)` for any non-VM resource.
 *
 * Keep each interface matching its own DTO — do not re-introduce inheritance between them.
 */
export interface ResourceSummary {
  id: string;
  /** Provider-side identifier (e.g. WiService VM UUID). Null while Provisioning/Error. */
  providerResourceId: string | null;
  name: string;
  type: ResourceType;
  status: ResourceStatus;
  tenantName: string;
  region: string;
  monthlyPrice: number;
  currency: string;
  createdAt: string;
  /** Paid-through (active billing-cycle end) timestamp. Null if no active billing cycle. */
  expiresAt: string | null;
  /** Order number (WIC-ORD-XXXXXX) this resource was created from. Null outside the order flow. */
  orderNumber: string | null;
  /** Status of the owning order (e.g. "Paid", "Complete"). Null when there is no owning order. */
  orderStatus: string | null;
  /**
   * What the owning order was raised for: "NewService", "Renewal", "Upgrade", "Addon", "Transfer"
   * or "Adjustment". Null when there is no owning order.
   */
  orderType: string | null;
  /** Email of the user who created the resource. Null when that user no longer exists. */
  createdByEmail: string | null;
}

/** Mirrors `ResourceListDto` — one row of `GET /api/v1/admin/resources`. */
/**
 * Body of `DELETE /api/v1/admin/resources/{id}`: what an operator fills in before a resource is
 * deleted. Re-checked by the backend.
 */
export interface DeleteResourcePayload {
  /**
   * Required for a VM and re-checked: it must survive trimming at 5+ characters. Storage, networks
   * and buckets are deleted without one.
   */
  reason?: string;
  /** Required for a VM and matched exactly by the backend (case-sensitive, spaces trimmed). */
  confirmationName?: string;
  /** Optional lapse filter for a full deletion, rechecked by the backend. */
  expiredDays?: number;
}

/**
 * Body of `DELETE /api/v1/admin/resources/{id}/record`: removes WiCloud's record of a VM and leaves
 * the machine running at the infrastructure — the escape hatch for a machine the provider has lost
 * or refuses to destroy. VMs only; both fields are required and re-checked by the backend.
 */
export interface DeleteResourceRecordPayload {
  reason: string;
  confirmationName: string;
}

/**
 * Body of `POST /api/v1/admin/resources/{id}/reinstall`. `templateId` is one of the admin OS image
 * catalog's ids; omitting it keeps the VM's current OS.
 *
 * `confirmationName` and `reason` are the in-place reinstall's own fields, required by the backend's
 * `RESOURCE_NOT_STOPPED`/`REINSTALL_REASON_REQUIRED`/`CONFIRMATION_NAME_MISMATCH` gates.
 */
export interface ReinstallResourcePayload {
  templateId?: string;
  /** The VM's name typed back, matched ordinally (surrounding spaces forgiven). */
  confirmationName: string;
  /** 5–500 characters, recorded in the audit row's metadata. */
  reason: string;
}

/** Branch the retry took — mirrors `RetryResourceProvisioningResultDto.Outcome`. */
export const RETRY_PROVISIONING_OUTCOMES = [
  "OrderFulfilmentRestarted",
  "ProvisioningRequeued",
  "FailureReset",
] as const;
export type RetryProvisioningOutcome =
  (typeof RETRY_PROVISIONING_OUTCOMES)[number];

/** Response of `POST /api/v1/admin/resources/{id}/retry-provisioning`. */
export interface RetryProvisioningResult {
  outcome: RetryProvisioningOutcome;
  /** True when the job re-checks a machine already recorded instead of asking for a new one. */
  resumesExistingVm: boolean;
  /** True when the order was moved from Failed back to Processing. */
  orderReopened: boolean;
  /** Attached disks and networks moved from Error back to Provisioning. */
  childrenReset: number;
  /**
   * The provider id dropped from the VM because the infrastructure no longer has that machine, so a
   * new one is created. Set only when the retry was sent with `recreateMissingVm`.
   */
  droppedVmUuid?: string | null;
}

/** What `useRetryResourceProvisioning` sends for one VM. */
export interface RetryProvisioningRequest {
  id: string;
  /**
   * The operator's tick: when the infrastructure no longer has the machine this VM records, drop
   * that id and create a new machine. The server asks the infrastructure itself before dropping.
   */
  recreateMissingVm: boolean;
}

/**
 * One entry of `POST /api/v1/admin/resources/retry-provisioning/check`. Whether "Retry
 * provisioning" may be offered right now for one resource — open to every admin role; only a
 * SuperAdmin may actually press Retry. An id the caller may not see comes back `allowed: false`
 * with `RESOURCE_NOT_FOUND`.
 */
export interface ProvisioningRetryCheck {
  allowed: boolean;
  /** Machine-readable refusal code (e.g. RETRY_WINDOW_EXPIRED), or null when allowed. */
  reasonCode: string | null;
  reason: string | null;
  /**
   * No provider id is recorded, so the last create request may have built a machine WiCloud never
   * heard back about. Computed server-side (a VM without a uuid); replaces the local
   * `mayAlreadyExistOnProvider` heuristic.
   */
  machineMayExist: boolean;
}

/** Shortest deletion reason the backend accepts - mirrors AdminDeleteResourceCommandValidator. */
export const DELETE_REASON_MIN_LENGTH = 5;

/** Longest deletion reason the backend accepts. */
export const DELETE_REASON_MAX_LENGTH = 500;

/**
 * Largest selection the bulk-stop endpoint accepts in one request.
 *
 * The console's largest page size, so an operator can select a full page and stop it.
 */
export const BULK_STOP_MAX_SELECTION = 100;

/** Longest note the bulk-stop endpoint records on each machine's audit entry. */
export const STOP_REASON_MAX_LENGTH = 500;

/** Body of `POST /api/v1/admin/resources/bulk-stop`. */
export interface BulkStopResourcesPayload {
  resourceIds: string[];
  /**
   * Optional note, up to `STOP_REASON_MAX_LENGTH`, recorded on the audit entry of every machine in
   * the batch. Optional because a stop is reversible — unlike the bulk delete, which demands one.
   */
  reason?: string;
}

/** What happened to one machine inside a bulk stop. */
export interface BulkStopResourceResult {
  resourceId: string;
  /** Empty only when the row could not be loaded at all. */
  resourceName: string;
  success: boolean;
  /**
   * The same codes the single stop endpoint returns, since every row runs through that handler:
   * RESOURCE_NOT_FOUND, UNSUPPORTED_RESOURCE_TYPE, METADATA_MISSING, INVALID_UUID, WISERVICE_ERROR,
   * POWER_ACTION_CONFLICT, STOP_FAILED.
   */
  errorCode: string | null;
  /** For a WISERVICE_ERROR this carries the provider's own sentence — read it, don't rewrite it. */
  errorMessage: string | null;
}

/**
 * The per-machine report a bulk stop answers with.
 *
 * Not all-or-nothing: each machine is its own webhook call, so one the hypervisor refuses does not
 * take the rest of the selection down, and the response names the ones that did not stop.
 */
export interface BulkStopResourcesResult {
  totalRequested: number;
  /** Accepted by WiService and written as Stopped — the status sync confirms the guest is down. */
  successCount: number;
  failureCount: number;
  results: BulkStopResourceResult[];
}

/** The tabs of the expired-VM clean-up list — mirrors the backend expired-VM group enum. */
export const EXPIRED_VM_GROUPS = ["Trial", "Paid", "Other"] as const;
export type ExpiredVmGroup = (typeof EXPIRED_VM_GROUPS)[number];

export const EXPIRED_VM_FUNDING_SOURCES = [
  "Payment",
  "SignupBonus",
  "Transfer",
] as const;
export type ExpiredVmFundingSource =
  (typeof EXPIRED_VM_FUNDING_SOURCES)[number];

export const EXPIRED_VM_CYCLE_STATUSES = [
  "Active",
  "Paused",
  "Suspended",
  "Expired",
  "Cancelled",
] as const;
export type ExpiredVmCycleStatus = (typeof EXPIRED_VM_CYCLE_STATUSES)[number];

/** One row of `GET /api/v1/admin/resources/expired` — mirrors the backend `ExpiredVmDto`. */
export interface ExpiredVm {
  id: string;
  name: string;
  status: ResourceStatus;
  vmUuid: string | null;
  ipAddress: string | null;
  tenantId: string;
  tenantName: string | null;
  userEmail: string | null;
  userPhone: string | null;
  /** UTC ISO; null when the VM has no expiry. */
  expiresAt: string | null;
  billingCycleId: string | null;
  fundingSource: ExpiredVmFundingSource | null;
  cycleStatus: ExpiredVmCycleStatus | null;
  cycleEnd: string | null;
  /** UTC ISO: when the deletion was started; null unless the VM is `Terminating`. */
  deletingSince: string | null;
  /** UTC ISO: when the last deletion failed and the VM was put back; null when none has since the last request. */
  deletionFailedAt: string | null;
}

/**
 * The only statuses the list ever holds (`GetExpiredVmsQuery.ListedStatuses`); the backend refuses any
 * other. `Terminating` rows are deletions already started, listed so they can be watched, not acted on.
 */
export const EXPIRED_VM_STATUS_OPTIONS: ResourceStatus[] = [
  "Running",
  "Stopped",
  "Suspended",
  "Error",
  "Terminating",
];

export function isStatusOption(value: string | null): value is ResourceStatus {
  return EXPIRED_VM_STATUS_OPTIONS.some((s) => s === value);
}

/** The "ended N+ days ago" choices; the backend accepts exactly these (`GetExpiredVmsQuery.AllowedExpiredDays`). */
export const EXPIRED_VM_DAYS = [1, 3, 5, 7, 15, 30] as const;

/** Each tab's lapse when none is chosen; the backend applies the same (`GetExpiredVmsQuery.DefaultDaysFor`). */
export const EXPIRED_VM_DEFAULT_DAYS: Record<ExpiredVmGroup, number> = {
  Trial: 15,
  Paid: 30,
  Other: 3,
};

export interface ExpiredVmFilters {
  group: ExpiredVmGroup;
  /** Narrows any of the three VM tabs. */
  status?: ResourceStatus;
  /** The term ended at least this many days ago (one of `EXPIRED_VM_DAYS`); omitted → the tab's default (`EXPIRED_VM_DEFAULT_DAYS`). */
  expiredDays?: number;
  page: number;
  pageSize: number;
}

export const PROVIDER_VM_STATUSES = [
  "Running",
  "Stopped",
  "Suspended",
] as const;
export type ProviderVmStatus = (typeof PROVIDER_VM_STATUSES)[number];

/**
 * What the infrastructure said about a VM — a value of `GET /api/v1/admin/resources/provider-states`,
 * keyed by resource id. Every field is null when the machine was not read (no machine on record, n8n
 * has no such machine, or n8n did not answer).
 */
export interface ResourceProviderState {
  providerStatus: ProviderVmStatus | null;
  powerStatus: string | null;
  qmpStatus: string | null;
  lock: string | null;
  /** The QEMU guest agent inside the VM ("online" / "offline"). */
  qemuAgent: string | null;
  /** n8n answered that it has no machine with the VM's UUID. */
  isNotFound: boolean;
}

/** Longest and shortest reason `POST /api/v1/admin/resources/expired/delete` accepts. */
export const EXPIRED_DELETE_REASON_MIN_LENGTH = 5;
export const EXPIRED_DELETE_REASON_MAX_LENGTH = 500;

/** Body of `POST /api/v1/admin/resources/expired/delete` and `expired/delete-record`. */
export interface DeleteExpiredVmsPayload {
  resourceIds: string[];
  reason: string;
  /** The lapse filter the list was read with: each VM is re-checked against it before deletion. */
  expiredDays?: number;
}

export interface DeleteExpiredVmResult {
  resourceId: string;
  resourceName: string;
  success: boolean;
  errorCode: string | null;
  /** The infrastructure's own sentence when it refused — shown verbatim. */
  errorMessage: string | null;
}

/** Answers 200 even when every row failed — read the counts, not the status. */
export interface DeleteExpiredVmsResult {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  results: DeleteExpiredVmResult[];
}

/** `GET /api/v1/admin/resources/expired/counts`: the VM tabs at their default lapse, orphans in full. */
export interface ExpiredVmCounts {
  trial: number;
  paid: number;
  other: number;
  orphans: number;
}

/** The fourth tab: not a backend `ExpiredVmGroup`, it lists disks and networks, not VMs. */
export const ORPHANS_TAB = "Orphans";
export type ExpiredTab = ExpiredVmGroup | typeof ORPHANS_TAB;

/** Which disks and networks the tab lists: only those that had a VM, or only those that never had one. */
export const ORPHAN_CHILD_ATTACHMENTS = ["FormerVm", "NeverAttached"] as const;
export type OrphanChildAttachment = (typeof ORPHAN_CHILD_ATTACHMENTS)[number];

/** The tab's "VM ended N+ days ago" when none is chosen; the backend applies the same (`GetOrphanChildrenQuery.DefaultVmEndedDays`). */
export const ORPHAN_DEFAULT_DAYS = 30;

export interface OrphanChildrenFilters {
  /** The former VM expired (deleted, with no expiry) at least this many days ago; one of `EXPIRED_VM_DAYS`. */
  vmEndedDays: number;
  /** Omitted: both kinds. */
  attachment?: OrphanChildAttachment;
}

export const ORPHAN_CHILD_TYPES = ["Storage", "Network"] as const;
export type OrphanChildType = (typeof ORPHAN_CHILD_TYPES)[number];

/** One row of `GET /api/v1/admin/resources/orphan-children`. */
export interface OrphanChild {
  id: string;
  name: string;
  type: OrphanChildType;
  status: ResourceStatus;
  tenantId: string;
  tenantName: string | null;
  userEmail: string | null;
  userPhone: string | null;
  createdAt: string;
  /** Null when the disk or network was never attached to a VM. */
  parentId: string | null;
  parentName: string | null;
  parentStatus: ResourceStatus | null;
  parentDeletedAt: string | null;
  /** That VM's expiry; null when it had none or the row was never attached. */
  parentExpiresAt: string | null;
  /** That VM's machine UUID at the infrastructure; null when it names none. */
  parentVmUuid: string | null;
  /** The VM's own billing cycle; all three null when it had none. */
  parentFundingSource: ExpiredVmFundingSource | null;
  parentCycleStatus: ExpiredVmCycleStatus | null;
  parentCycleEnd: string | null;
}

/** Body of `POST /api/v1/admin/resources/orphan-children/remove` (1–10 unique ids; reason limits as for the VM delete). */
export interface RemoveOrphanChildrenPayload {
  resourceIds: string[];
  reason: string;
  /** The lapse the list was read with, so each row is re-checked under the same rule. */
  vmEndedDays: number;
}

/** Same report shape as the VM delete; codes: RESOURCE_NOT_FOUND, NOT_ORPHAN, VM_STILL_ON_PROVIDER, PROVIDER_UNAVAILABLE, REMOVE_CONFLICT, REMOVE_FAILED. */
export type RemoveOrphanChildrenResult = DeleteExpiredVmsResult;

/**
 * The derived resource columns the backend can fill for rows saved before the column existed.
 * Mirrors the backend `ResourceBackfillField` enum.
 */
export const RESOURCE_BACKFILL_FIELDS = ["ProductLineSlug"] as const;
export type ResourceBackfillField = (typeof RESOURCE_BACKFILL_FIELDS)[number];

/** What one run of `POST /admin/resources/backfill/{field}` did. */
export interface ResourceFieldBackfillResult {
  field: ResourceBackfillField;
  /** Rows whose field was still empty when the run reached them. */
  scannedCount: number;
  /** Rows given a value; the rest record nothing to fill the field from and stay empty. */
  updatedCount: number;
}

export interface Resource extends ResourceSummary {
  tenantId: string;
  /** Email of the user who created this resource. */
  userEmail: string;
  /** Phone of the user who created this resource, if provided. */
  userPhoneNumber: string | null;
  /** Display name (first + last) of the user who created this resource; empty when none is on file. */
  userFullName: string | null;
  /**
   * Public IPv4 of the VM, no CIDR suffix. Null for non-VM resources and for a VM that has no
   * public address in its metadata yet. The detail endpoint carries the same value under
   * `vm.publicIpAddress` — this one is list-only, hence not on `ResourceSummary`.
   */
  publicIpAddress: string | null;
  /** Private IPv4 of the VM, no CIDR suffix. Null for non-VM resources or when unset. */
  privateIpAddress: string | null;
  /**
   * vCPU cores. Null for a non-VM resource, and for a VM where neither the specs nor the
   * provisioning metadata records one. Same name as `ResourceDetail`'s `vm.vcpu` — one number,
   * one name, across both views.
   */
  vcpu: number | null;
  /** Memory in **MB** — the unit the backend stores. Null on the same terms as `vcpu`. */
  memoryMb: number | null;
  /**
   * Total disk in GB across the VM's disks. Null on the same terms as `vcpu`, and also while a VM
   * is still provisioning and no size is known yet — never 0.
   */
  diskSizeGb: number | null;
  /**
   * What an attached disk or network IS, in one line: "30 GB ssd", "VLAN 1351". Null for a VM,
   * whose size and address have columns of their own, and for a child that has recorded neither.
   *
   * Every field above is VM-only, so a Disk or Network row read as a dash in every column while
   * its own JSON held the size, the tier and the VLAN all along.
   */
  attachmentSummary: string | null;
  /**
   * OS / template the VM was cloned from, e.g. "Ubuntu-24.04", "Windows-Server-2025". Null for a
   * non-VM resource and for a VM whose template name was never recorded.
   */
  operatingSystem: string | null;
  /**
   * CPU chip type the VM runs on — "Intel" or "AMD". Null for a non-VM resource and for any VM
   * ordered before the wizard offered the choice, which is roughly half the existing rows.
   */
  chipType: string | null;
  /**
   * Product-line slug recorded on the VM: "cheap", "eco", "standard", "performance" — the line it
   * was ordered on, or the line the admin product-line move last set. Null on the same terms as
   * `chipType` — the two together are what resolve the WiService pool.
   */
  productLine: string | null;
  /**
   * WiService pool recorded for the VM, e.g. "ws-t4-i" — the one it was sent to at creation, or the
   * pool of the line the admin product-line move last set — or resolved from `productLine` +
   * `chipType` for a VM with none recorded. Null for a non-VM resource
   * and when nothing resolves (WiService then used its own default pool).
   */
  poolName: string | null;
  /**
   * Until when a deleted VM is kept before it is finalized (UTC, possibly without a trailing `Z`); null
   * for a row that is not a deletion in progress — live, being reinstalled, or already finalized.
   * Past it, a SuperAdmin may finalize the row by hand. Mirrors `Resource.DeletionKeptUntil()`.
   */
  restorableUntil?: string | null;
  /**
   * When the resource was deleted (UTC, possibly without a trailing `Z`); null while it is live or
   * being reinstalled. The list shows deleted rows next to live ones (2026-10-14), so this is what
   * tells them apart.
   */
  deletedAt?: string | null;
}

/**
 * Mirrors `ResourceDetailDto`. These three fields arrive as **JSON strings**, not objects — the
 * backend stores them as JSON columns and passes them through verbatim. They were previously typed
 * as objects, which made `typeof specifications === "object"` false at runtime (so specs never
 * rendered) and `Object.entries(tags)` iterate the characters of the string `"[]"`.
 *
 * Parse with the helpers in `@/lib/provisioning` rather than casting.
 */
export interface ResourceDetail extends ResourceSummary {
  /** JSON object string, e.g. `{"cores":2,"memory":2048}`. */
  specifications: string;
  /** JSON array string, e.g. `["prod","team-a"]`. */
  tags: string;
  /** JSON object string holding operational metadata. */
  metadata: string;
  vm?: VmSystemInfo | null;
  /** Timestamp the resource finished provisioning (went live on the provider). Null until then. */
  provisionedAt?: string | null;
  /** Last write to the resource row. Detail endpoint only — the list DTO does not carry it. */
  updatedAt: string;
  /** Hourly price in `currency`. */
  hourlyCost: number;
  /** CPU chip type the VM was ordered on ("Intel", "AMD"). Null for a non-VM or older VM. */
  chipType: string | null;
  /** Product-line slug from the provisioning configuration ("cheap"); follows an admin product-line move. Null on the same terms. */
  productLine: string | null;
  /** WiService pool recorded for the VM (at creation or by an admin product-line move, else resolved), e.g. "ws-t4-i". Null when nothing resolves. */
  poolName: string | null;
  /**
   * When the VM's last deletion failed for good at the infrastructure and the VM was put back (UTC
   * ISO). Null when no deletion has failed since the last delete request, and on non-VM rows.
   */
  deletionFailedAt?: string | null;
  product?: {
    id: string;
    name: string;
    sku: string;
    categoryName: string;
  };
}

/**
 * Field keys the configuration comparison covers. Same spelling as the backend
 * `Specifications` keys, because the apply endpoint takes these verbatim.
 */
export const SPEC_MISMATCH_FIELD_KEYS = ["cores", "memory", "disk"] as const;
export type SpecMismatchFieldKey = (typeof SPEC_MISMATCH_FIELD_KEYS)[number];

/** Mirrors backend `VmSpecMismatchStatus` — PascalCase, exactly as serialized. */
export const SPEC_MISMATCH_STATUSES = [
  "Match",
  "Mismatch",
  "MissingLocal",
  "MissingProvider",
] as const;
export type SpecMismatchStatus = (typeof SPEC_MISMATCH_STATUSES)[number];

/** One compared configuration value. Mirrors `ResourceSpecMismatchFieldDto`. */
export interface SpecMismatchField {
  key: string;
  label: string;
  /** Unit both values are in ("MB", "GB"), or null for a plain count. */
  unit: string | null;
  /** What WiCloud shows today, or null when it holds nothing. */
  wiCloudValue: number | null;
  /** What the infrastructure reports, or null when it did not report this value. */
  providerValue: number | null;
  status: SpecMismatchStatus;
  /** False when there is nothing to copy — the provider reported no value for this field. */
  canApply: boolean;
}

/**
 * A live comparison between the configuration WiCloud stores and the one the infrastructure
 * reports. Mirrors `ResourceSpecMismatchDto`.
 *
 * `providerReachable: false` is a normal answer, not an error: the VM may have no UUID yet or the
 * provider may be down. `providerError` then says why, and every field's provider side is null.
 */
export interface ResourceSpecMismatch {
  resourceId: string;
  resourceName: string;
  vmUuid: string | null;
  /** ISO timestamp of when the provider was asked. */
  checkedAt: string;
  providerReachable: boolean;
  providerError: string | null;
  resourceStatus: string;
  /** Power state the provider reports, shown for context only. */
  providerPowerState: string | null;
  hasMismatch: boolean;
  fields: SpecMismatchField[];
}

/**
 * Why a machine appears in the product-line audit. Mirrors the backend's `Issue` strings.
 *
 * - `None` — the pricing engine resolves a product line for it; nothing to do.
 * - `NotStated` — the specifications name no product line the engine can read, so it asks for the
 *   `Standard` tier by default.
 * - `Unresolved` — they name one that is no longer on sale.
 *
 * The last two both mean the same thing in practice: every re-price falls through to the legacy
 * component-price table instead of the catalogue.
 */
export const PRODUCT_LINE_ISSUES = ["None", "NotStated", "Unresolved"] as const;
export type ProductLineIssue = (typeof PRODUCT_LINE_ISSUES)[number];

/**
 * One billed line of a priced stack. Mirrors `ResourceStackPricingLine`.
 */
export interface ResourceStackPricingLine {
  resourceId: string;
  name: string;
  type: string;
  /**
   * Null when the catalogue could not price it. A deliberate zero is a real answer: another line
   * pays for what this one carries.
   */
  monthlyPrice: number | null;
  /** True when this line fell through to the legacy component prices, which refuses the renewal. */
  fellThrough: boolean;
  /**
   * Why the catalogue could not price this line, straight from the pricing engine, or null when it
   * priced. The reason is the actionable part — "Backups must be between 0 and 0 slot for product
   * line 'Cheap' (requested 2 slot)" tells an operator what to fix.
   */
  error: string | null;
}

/**
 * A machine's stack priced against ONE product line. Mirrors `ResourceStackPricing`.
 */
export interface ResourceStackPricing {
  resourceId: string;
  currency: string;
  /** Null when the stack was priced exactly as its rows record it. */
  productLine: string | null;
  /**
   * A TOTAL only when `anyLineUnpriced` is false — an unpriced line contributes nothing, so
   * otherwise this is a sum with a hole in it and must not be shown as a price.
   */
  monthlyPrice: number;
  lines: ResourceStackPricingLine[];
  anyLineFellThrough: boolean;
  /** Any line has no catalogue rate: it fell through, or the line refused the configuration. */
  anyLineUnpriced: boolean;
}

/**
 * One product line still on sale and what the machine's stack would cost on it.
 * Mirrors `ResourceProductLineQuoteCandidateDto`.
 */
export interface ResourceProductLineQuoteCandidate {
  /** Slug to send to the update endpoint ("cheap", "performance"). */
  slug: string;
  tier: string;
  name: string;
  pricing: ResourceStackPricing;
}

/**
 * What one machine's whole stack costs per month as it stands, and on each product line still on
 * sale. Undiscounted monthly list rates: no commitment discount, no VAT.
 * Mirrors `ResourceProductLineQuoteDto`.
 */
export interface ResourceProductLineQuote {
  resourceId: string;
  name: string;
  currency: string;
  /** The VM row's own stored rate — not comparable with the stack totals beside it. */
  recordedMonthlyPrice: number;
  current: ResourceStackPricing;
  candidates: ResourceProductLineQuoteCandidate[];
}

/**
 * One line of a machine's stack — the VM itself, or a disk or network attached to it — and what the
 * pricing engine makes of the product line stored on it.
 *
 * A child does NOT simply inherit the VM's: the renewal quote fills only the keys a child does not
 * already have, and checkout writes the product line onto the disk and the network too. So a
 * machine whose VM reads clean can still be refused by its own disk.
 * Mirrors `ResourceProductLineLineDto`.
 */
export interface ResourceProductLineLine {
  resourceId: string;
  name: string;
  type: string;
  storedProductLine: string | null;
  requestedTier: string;
  /** Null when this line falls through, which refuses the renewal for the whole machine. */
  resolvedProductLine: string | null;
}

/**
 * One VM and what the pricing engine makes of its stored product line.
 * Mirrors `ResourceProductLineAuditDto`.
 */
export interface ResourceProductLineAuditRow {
  resourceId: string;
  name: string;
  status: ResourceStatus;
  vmUuid: string | null;
  tenantId: string;
  tenantName: string | null;
  ownerEmail: string | null;
  /** As stored: "0…" or "+84…". Null when the owner has none. */
  ownerPhoneNumber: string | null;
  expiresAt: string | null;
  /**
   * What the pricing engine reads out of the stored specifications, or null when it reads nothing.
   * A value the engine drops — a number, a differently-cased key — is null here too.
   */
  storedProductLine: string | null;
  /**
   * The product line recorded in the nested `configuration` block, which with the chip type
   * resolves the pool. A move sets it to the chosen line. On a row with no pricing product line it
   * is usually the only surviving record of the tier the VM was sold on.
   */
  provisioningProductLine: string | null;
  /** WiService pool the VM was sent to (recorded at creation, else resolved), e.g. "ws-t4-i". Null when nothing resolves. */
  poolName: string | null;
  /** The verdict across the WHOLE stack, taken from the first line that does not resolve. */
  issue: ProductLineIssue;
  /** The tier the engine asks the catalogue for. `Standard` on a `NotStated` row. */
  requestedTier: string;
  /** Slug of the line it actually prices against today, or null when it falls through. */
  resolvedProductLine: string | null;
  /** Every line a renewal would price: the VM first, then each live disk and network. */
  lines: ResourceProductLineLine[];
  cores: number | null;
  memoryGb: number | null;
  diskGb: number | null;
  /** The rate stored on the resource row. Nothing in this feature changes it. */
  recordedMonthlyPrice: number;
  currency: string;
}

/**
 * What a product-line correction actually wrote. Mirrors `ResourceProductLineUpdateDto`.
 */
export interface ResourceProductLineUpdate {
  resourceId: string;
  name: string;
  previousProductLine: string | null;
  productLine: string;
  tier: string;
  recordedMonthlyPrice: number;
  /**
   * Every line the call actually wrote — the VM and each attached disk or network that was not
   * already on the chosen product line. Empty when all of them already were.
   */
  updatedLines: Array<{ resourceId: string; name: string; type: string }>;
}

/**
 * One disk as the infrastructure reports it. Mirrors `ResourceConfigDiskDto`.
 */
export interface ResourceConfigDisk {
  /** Provider device name, e.g. "scsi0". */
  device: string;
  sizeGb: number;
  /**
   * True when this device is an add-on storage resource with its own price and billing cycle. Its
   * capacity is left out of the VM's price and it cannot be resized from this screen — that would
   * hand the customer capacity nothing charges for.
   */
  billedSeparately: boolean;
}

/**
 * A complete VM configuration — one side of a before/after pair.
 * Mirrors `ResourceConfigSnapshotDto`.
 */
export interface ResourceConfigSnapshot {
  cores: number;
  memoryMb: number;
  /** Sum of every attached disk, add-on disks included. */
  diskGb: number;
  /** The capacity the VM itself is priced on — `diskGb` less any add-on disks. */
  ownDiskGb: number;
  disks: ResourceConfigDisk[];
}

/**
 * The range the VM's product line allows, which the quote and every apply step refuse a target
 * outside of. The dialog draws its controls from it and keeps no ceiling of its own. `diskGb`
 * bounds the whole attached capacity, add-on disks included. Mirrors
 * `ResourceConfigChangeLimitsDto`.
 */
export interface ResourceConfigChangeLimits {
  cores: SpecRangeDto;
  memoryGb: SpecRangeDto;
  diskGb: SpecRangeDto;
}

/** Which provider call applies a block. Mirrors the backend `VmConfigChangeBlockKind` enum. */
export const CONFIG_CHANGE_BLOCK_KINDS = ["Cores", "Memory", "Disk"] as const;
export type ConfigChangeBlockKind = (typeof CONFIG_CHANGE_BLOCK_KINDS)[number];

/**
 * One block of a configuration change: a single provider call, priced and applied on its own.
 * Mirrors `ResourceConfigChangeBlockDto`.
 */
export interface ResourceConfigChangeBlock {
  /** Stable identifier: "cores", "memory", or "disk:scsi1". */
  key: string;
  kind: ConfigChangeBlockKind;
  /** Provider disk device, or null for cpu/memory. */
  device: string | null;
  label: string;
  /** Unit both values are in ("MB", "GB"), or null for a plain count. */
  unit: string | null;
  currentValue: number;
  targetValue: number;
  /** What is actually sent to the provider — for a disk, the increase rather than the new total. */
  providerPayloadValue: number;
  /** True when the provider refuses this block while the VM is running (cpu and memory). */
  requiresStoppedVm: boolean;
  canApply: boolean;
  blockedReason: string | null;
  /** What this block alone adds to the monthly price, in the smallest currency unit. */
  monthlyDelta: number;
  /** This block's share of the amount due now, excluding VAT. Nothing is collected by this flow. */
  amountDue: number;
}

/**
 * What a requested configuration change would cost and what it would take to apply.
 * Mirrors `ResourceConfigChangeQuoteDto`. All money is in the smallest unit of `currency`.
 */
export interface ResourceConfigChangeQuote {
  resourceId: string;
  resourceName: string;
  vmUuid: string | null;
  /** When the infrastructure was asked. A quote is only as fresh as this. */
  checkedAt: string;
  resourceStatus: string;
  providerPowerState: string | null;
  /** True when the infrastructure reports the VM running, which blocks any cpu/memory change. */
  providerReportsRunning: boolean;
  /** The product line tier the price was computed from. */
  productLine: string | null;
  currency: string;
  current: ResourceConfigSnapshot;
  target: ResourceConfigSnapshot;
  blocks: ResourceConfigChangeBlock[];
  /** What the resource actually charges today, which is what its next renewal bills. */
  recordedMonthlyPrice: number;
  /** What the current configuration is worth at today's rates — the basis of the difference. */
  currentMonthlyPrice: number;
  newMonthlyPrice: number;
  monthlyDifference: number;
  /**
   * True when the resource has an expiry still ahead, so the change is charged up to it:
   * `amountDue` = `dailyDifference` x `remainingDays`. False when no expiry is on record or it has
   * already elapsed: `amountDue` is then one month's difference and the fields below are 0 / null.
   */
  hasTerm: boolean;
  /** When the resource expires (UTC), or null when `hasTerm` is false. */
  expiresAt: string | null;
  /** Commitment term the daily rate is taken at; 1 for a monthly or day-based term. */
  termMonths: number;
  /** What the change adds (or removes) per day, at the term's rate. */
  dailyDifference: number;
  /** Whole days until the resource expires; a started day counts. */
  remainingDays: number;
  /** Net due now, before VAT — a figure to quote, not a charge. Negative when the price drops. */
  amountDue: number;
  /** VAT rate as a percentage (10 means 10%). */
  vatRate: number;
  tax: number;
  /** The figure quoted to the customer, VAT included. */
  total: number;
  canApplyAll: boolean;
}

/**
 * The outcome of one block the apply step attempted. Mirrors `ResourceConfigChangeStepResultDto`.
 * Every attempted block gets one, whether it landed or not.
 */
export interface ResourceConfigChangeStepResult {
  key: string;
  label: string;
  unit: string | null;
  previousValue: number;
  newValue: number;
  succeeded: boolean;
  /** The n8n execution id, when the provider supplied one. */
  executionId: number | null;
  providerMessage: string | null;
}

/**
 * What one apply request did to the infrastructure. Mirrors `ResourceConfigChangeApplyResultDto`.
 *
 * A partial outcome is a normal answer: the provider calls are independent, so vCPU and memory can
 * land while a disk refuses.
 */
export interface ResourceConfigChangeApplyResult {
  resourceId: string;
  vmUuid: string | null;
  allApplied: boolean;
  steps: ResourceConfigChangeStepResult[];
  /** The configuration in force before this request — hand it back to the commit call. */
  previous: ResourceConfigSnapshot;
}

/**
 * What the final step recorded. Mirrors `ResourceConfigChangeCommitDto`.
 *
 * No payment is created by this flow — an admin changing a customer's configuration moves no money
 * through WiCloud. Payments belong to the customer's own checkout.
 */
export interface ResourceConfigChangeCommit {
  resourceId: string;
  previousMonthlyPrice: number;
  /** What the resource carries now, and renews at. */
  newMonthlyPrice: number;
  /**
   * The difference from today up to the expiry date, VAT included. **Not collected** — the
   * figure to quote the customer or invoice separately. Zero or negative when there is none.
   */
  amountToInvoice: number;
  currency: string;
  /** What is left for the operator to do about the money, in a sentence. */
  note: string;
  comparison: ResourceSpecMismatch | null;
}

/**
 * How far a one-press configuration change got. Mirrors the backend `ResourceConfigChangeOutcome`.
 *
 * Only two of these are finished states. `AppliedNotPriced` is the one that costs money if it is
 * left alone: the VM and the record moved and the price did not, so the next renewal undercharges.
 */
export const CONFIG_CHANGE_OUTCOMES = [
  "Completed",
  "PartiallyApplied",
  "PendingInfrastructure",
  "AppliedNotRecorded",
  "AppliedNotPriced",
] as const;
export type ConfigChangeOutcome = (typeof CONFIG_CHANGE_OUTCOMES)[number];

/**
 * What one confirmed configuration change did, end to end.
 * Mirrors `ResourceConfigChangeExecutionDto`.
 *
 * A result comes back whenever the infrastructure was touched at all, even when a later step
 * failed — the VM really has changed, so an error would say the opposite. `outcome` is what says
 * how far it got and `warning` what is left to do.
 */
export interface ResourceConfigChangeExecution {
  resourceId: string;
  vmUuid: string | null;
  outcome: ConfigChangeOutcome;
  /** True when every block landed on the infrastructure. */
  allApplied: boolean;
  /** True when the resource now carries its new price. */
  settled: boolean;
  steps: ResourceConfigChangeStepResult[];
  /** The configuration in force before the change. */
  previous: ResourceConfigSnapshot;
  /** What the reprice recorded, or null when it did not run. */
  settlement: ResourceConfigChangeCommit | null;
  comparison: ResourceSpecMismatch | null;
  /** A plain sentence describing the outcome, ready to show as-is. */
  summary: string;
  /** What the operator still has to do, or null when nothing is outstanding. */
  warning: string | null;
}

/** The target configuration sent to the quote endpoint. Every field is optional. */
export interface ResourceConfigChangeTarget {
  cores?: number;
  memoryMb?: number;
  /** Target size in GB per existing device, keyed by device name. */
  disks?: Record<string, number>;
}

export interface VmSystemInfo {
  uuid?: string | null;
  operatingSystem?: string | null;
  vcpu?: number | null;
  memoryMb?: number | null;
  diskSizeGb?: number | null;
  diskTypes: string[];
  publicIpAddress?: string | null;
  privateIpAddress?: string | null;
  vlans: number[];
}

/**
 * Mirrors backend `ResourceSnapshotDto`.
 */
export const RESOURCE_SNAPSHOT_STATUSES = [
  "creating",
  "available",
  "failed",
  "deleting",
  "restoring",
] as const;
export type ResourceSnapshotStatus =
  (typeof RESOURCE_SNAPSHOT_STATUSES)[number];

export interface ResourceSnapshot {
  id: string;
  resourceId: string;
  name: string;
  description?: string | null;
  size: number;
  status: ResourceSnapshotStatus;
  isAutomatic: boolean;
  snaptime?: number | null;
  parent?: string | null;
  vmstate: number;
  createdAt: string;
}

export interface ResourceMetrics {
  resourceId: string;
  timestamp: string;
  cpu: number;
  memory: number;
  disk: number;
  network: number;
}

/** One sample of a metrics series. */
export interface MetricDataPoint {
  /** ISO 8601 timestamp. */
  timestamp: string;
  value: number;
}

/**
 * Mirrors backend admin `ResourceMetricsDto`: the client's `ResourceMetricsTimeSeriesDto` (built by
 * the same backend code) plus `hostCpuUsage`. A sample missing a metric is left out of that series.
 */
export interface ResourceMetricsResponse {
  resourceId: string;
  timeframe: string;
  /** CPU % as the customer sees it: divided by the VmCpuLimitRatio setting, capped at 100. */
  cpuUsage: MetricDataPoint[];
  /** CPU % exactly as WiService measured it on the host. Equal to `cpuUsage` while no ratio is set. */
  hostCpuUsage: MetricDataPoint[];
  memoryUsageMB: MetricDataPoint[];
  memoryUsagePercent: MetricDataPoint[];
  /** Mbps (megabits per second). */
  networkInMbps: MetricDataPoint[];
  networkOutMbps: MetricDataPoint[];
  /** Bytes per second. */
  diskReadBytesPerSec: MetricDataPoint[];
  diskWriteBytesPerSec: MetricDataPoint[];
  diskUsagePercent: MetricDataPoint[];
  totalCores: number;
  totalMemoryMB: number;
  totalDiskGB: number;
  startTime: string;
  endTime: string;
}

/**
 * What the admin resource charts plot — the client app's `ResourceMetrics` shape (same units: %,
 * Mbps, MB/s) plus `hostCores`, the CPU series before the limit-ratio rescale.
 */
export interface ResourceMetricsSeries {
  /** CPU % as the customer sees it. */
  cores: MetricDataPoint[];
  /** CPU % as measured on the host. */
  hostCores: MetricDataPoint[];
  /** Memory %. */
  memory: MetricDataPoint[];
  /** Mbps. */
  network: {
    inbound: MetricDataPoint[];
    outbound: MetricDataPoint[];
  };
  /** MB/s. */
  disk: {
    read: MetricDataPoint[];
    write: MetricDataPoint[];
  };
}

/**
 * Mirrors backend `ResourceCostAnalysisDto`. The previous shape (`period`/`breakdown` with a
 * `component` field) didn't match any field the backend actually returns — `costAnalysis.breakdown`
 * was always `undefined`, crashing every `.map()` call over it as soon as the Cost Analysis tab
 * was reached.
 */
export interface CostAnalysis {
  resourceId: string;
  resourceName: string;
  periodStart: string;
  periodEnd: string;
  totalCost: number;
  currency: string;
  hourlyRate: number;
  monthlyRate: number;
  hoursActive: number;
  components: CostComponent[];
  dailyTrend: DailyCost[];
}

export interface CostComponent {
  name: string;
  cost: number;
  percentage: number;
  unit: string;
  quantity: number;
}

export interface DailyCost {
  date: string;
  cost: number;
  hoursActive: number;
}

/**
 * One entry of the WiService (n8n) probe catalog. Mirrors backend `ResourceN8nEndpointDto`.
 * The list is served by the API rather than hard-coded here so the console can never offer a
 * probe the backend whitelist would reject.
 */
export interface N8nEndpoint {
  key: string;
  label: string;
  description: string;
  method: string;
  pathTemplate: string;
  group: "vm" | "global";
  requiresUuid: boolean;
  supportsTimeframe: boolean;
  timeframes: string[];
  hasSideEffect: boolean;
}

/**
 * WiCloud's own stored view of a resource, returned with every probe so both sides of the same VM
 * can be read at once. Mirrors backend `ResourceN8nLocalStateDto`. The three JSON columns arrive
 * pre-parsed; `metadata` has its credential-bearing keys redacted server-side.
 */
export interface N8nLocalState {
  resourceId: string;
  tenantId: string;
  name: string;
  type: string;
  status: string;
  providerType: string;
  providerResourceId: string | null;
  region: string;
  vmUuid: string | null;
  specifications: unknown;
  configuration: unknown;
  metadata: unknown;
  provisionedAt: string | null;
  suspendedAt: string | null;
  expiresAt: string | null;
  terminatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Result of one probe. Mirrors backend `ResourceN8nProbeDto`.
 *
 * `response` is the provider's body passed through verbatim — provider casing, provider shape,
 * including fields no typed DTO in this codebase declares. `providerSuccess` is deliberately
 * nullable: a body with no `success` field states nothing, which is a different fault from an
 * explicit refusal. `transportError` covers both "no response arrived" and "nothing was sent"
 * (not a VM, no VM UUID stored, a SIMULATED- placeholder id).
 */
export interface N8nProbeResult {
  key: string;
  label: string;
  method: string;
  path: string;
  vmUuid: string | null;
  timeframe: string | null;
  requestedAt: string;
  durationMs: number;
  httpStatusCode: number | null;
  contentType: string | null;
  transportError: string | null;
  providerSuccess: boolean | null;
  providerMessage: string | null;
  response: unknown;
  responseText: string | null;
  local: N8nLocalState;
}

/**
 * Action lifecycle status. Mirrors the backend `ActionStatus` enum exactly — see
 * `WiCloud.Domain/Enums/ActionStatus.cs`. The previous type invented a `"Success"` value that the
 * backend never emits, so every row rendered as a failure.
 */
export const ACTION_STATUSES = [
  "Pending",
  "InProgress",
  "Completed",
  "Failed",
  "Cancelled",
] as const;
export type ActionStatus = (typeof ACTION_STATUSES)[number];

/** Action type. Mirrors the backend `ActionType` enum (`WiCloud.Domain/Enums/ActionType.cs`). */
export const ACTION_TYPES = [
  "Provision",
  "Start",
  "Stop",
  "Restart",
  "Suspend",
  "Resume",
  "Terminate",
  "Snapshot",
  "Restore",
  "Resize",
  "Backup",
  "Clone",
  "Migrate",
] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

/**
 * Mirrors `ResourceActionDto` from the Admin API. Every field name here matches the wire format —
 * the previous shape (`action`, `performedBy`, `performedAt`, `details`) matched nothing the
 * backend sends, so the Actions table rendered `undefined` in every column.
 */
export interface ResourceAction {
  id: string;
  resourceId: string;
  userId: string;
  userEmail: string;
  type: ActionType;
  status: ActionStatus;
  description?: string | null;
  /** JSON string of the request parameters. */
  parameters: string;
  /** JSON string of the result payload; null while pending or on failure. */
  result?: string | null;
  errorMessage?: string | null;
  /** Raw operational metadata JSON: clone progress, polling counters, provider failure evidence. */
  metadata: string;
  startedAt?: string | null;
  completedAt?: string | null;
  durationMs?: number | null;
  createdAt: string;
}

/**
 * One row from GET /api/v1/admin/resource-actions — the cross-resource view of the same
 * `resource_actions` trail {@link ResourceAction} reads one machine at a time. Carries the
 * resource, tenant and user context {@link ResourceAction} leaves to the caller, since this list
 * has no resource id of its own to assume.
 */
export interface ResourceActionListItem {
  id: string;
  resourceId: string;
  resourceName: string;
  resourceType: ResourceType;
  /** The VM's provider UUID, or null for a non-VM resource, or a VM never provisioned. */
  vmUuid: string | null;
  /**
   * True when the resource row itself has since been deleted. `resourceName`/`resourceType` are
   * historical in that case, and the resource-detail link is withheld rather than linking into a
   * row that is gone.
   */
  isResourceDeleted: boolean;
  tenantId: string;
  tenantName: string;
  userId: string;
  userEmail: string;
  type: ActionType;
  status: ActionStatus;
  description: string | null;
  /** JSON string of the request parameters. */
  parameters: string;
  /** JSON string of the result payload; null while pending or on failure. */
  result: string | null;
  errorMessage: string | null;
  /** Raw operational metadata JSON: clone progress, polling counters, provider failure evidence. */
  metadata: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Query string for GET /api/v1/admin/resource-actions.
 *
 * `search` matches ONLY a GUID (action/resource/tenant/user id — never the VM's provider UUID,
 * which is display-only) or a complete e-mail — never a substring of `description` or
 * `errorMessage` (2026-09-11 decision).
 */
export interface ResourceActionListFilters {
  search?: string;
  tenantId?: string;
  resourceId?: string;
  /** ISO 8601 with the browser's UTC offset — the backend expands it to the whole local day. */
  createdFrom?: string;
  createdTo?: string;
  page: number;
  pageSize: number;
}

/** Whether a VM was on a trial or a paid term. Mirrors the backend `VmPlanType` enum. */
export const VM_PLAN_TYPES = ["Trial", "Paid"] as const;
export type VmPlanType = (typeof VM_PLAN_TYPES)[number];

/**
 * One row from GET /api/v1/admin/resource-reinstalls: an in-place reinstall as it was recorded when
 * it was requested. The VM's name, UUID and address are the row's own copy, so they stay as they
 * were after the VM is renamed, re-addressed or deleted. Completed rows count against the
 * reinstall limit, whoever started them.
 */
export interface ResourceReinstallListItem {
  id: string;
  resourceId: string;
  vmName: string;
  providerVmUuid: string;
  ipAddress: string | null;
  tenantId: string;
  /** The tenant's current name; null when the tenant no longer exists. */
  tenantName: string | null;
  /** Who started it: the customer, or the operator. */
  userId: string;
  /** That user's current e-mail. */
  userEmail: string;
  /** That user's current phone number; null when the account has none. */
  userPhoneNumber: string | null;
  vmPlan: VmPlanType;
  /** Only InProgress, Completed and Failed are written for a reinstall. */
  status: ActionStatus;
  requestedAt: string;
  finishedAt: string | null;
}

/**
 * Query string for GET /api/v1/admin/resource-reinstalls. `search` is routed by its shape on the
 * server: a GUID (reinstall, VM, tenant or VM UUID) and an IP address match exactly; anything else
 * is a fragment of the VM name or e-mail, or a phone number. E-mail and phone are those of whoever
 * started the reinstall.
 */
export interface ResourceReinstallListFilters {
  search?: string;
  vmPlan?: VmPlanType;
  /** ISO 8601 with the browser's UTC offset — the backend expands it to the whole local day. */
  requestedFrom?: string;
  requestedTo?: string;
  page: number;
  pageSize: number;
}

/**
 * Query string for GET /api/v1/admin/resource-reinstalls/report. Unlike the list, both days are
 * required and may be at most `RESOURCE_REINSTALL_REPORT_MAX_DAYS` apart: the report aggregates
 * every reinstall of the window. `search` and `vmPlan` narrow it exactly as they do the list.
 */
export interface ResourceReinstallReportFilters {
  search?: string;
  vmPlan?: VmPlanType;
  /** ISO 8601 with the browser's UTC offset — the backend expands it to the whole local day. */
  requestedFrom: string;
  requestedTo: string;
}

/** The longest window the report endpoint accepts. Mirrors `GetResourceReinstallReportQuery.MaxRangeDays`. */
export const RESOURCE_REINSTALL_REPORT_MAX_DAYS = 366;

/** Headline figures of the report. The plan split and the status split each add up to `total`. */
export interface ResourceReinstallReportTotals {
  total: number;
  /** Only completed reinstalls count against the reinstall limit. */
  completed: number;
  failed: number;
  inProgress: number;
  trial: number;
  paid: number;
  /** Distinct VMs reinstalled, and the distinct tenants they belong to. */
  vmCount: number;
  tenantCount: number;
}

/** The reinstalls requested on one calendar day (`date` is "yyyy-MM-dd", the operator's day). */
export interface ResourceReinstallReportDay {
  date: string;
  trial: number;
  paid: number;
}

/**
 * One VM of the report's ranking. Name and UUID are the reinstall rows' own copies, so a VM since
 * renamed or deleted is still named as it was.
 */
export interface ResourceReinstallReportVm {
  resourceId: string;
  vmName: string;
  providerVmUuid: string;
  tenantId: string;
  /** The tenant's current name; null when the tenant no longer exists. */
  tenantName: string | null;
  total: number;
  trial: number;
  paid: number;
  completed: number;
  failed: number;
  lastRequestedAt: string;
}

/** One tenant of the report's ranking. */
export interface ResourceReinstallReportTenant {
  tenantId: string;
  tenantName: string | null;
  /** The owner's contact; null when the tenant or its owner no longer exists, or no phone was given. */
  ownerEmail: string | null;
  ownerPhoneNumber: string | null;
  /** Distinct VMs the tenant reinstalled in the window. */
  vmCount: number;
  total: number;
  trial: number;
  paid: number;
}

/**
 * GET /api/v1/admin/resource-reinstalls/report: every part is computed over the same reinstalls.
 * `days` holds every day of the window, oldest first, with zeros where nothing happened.
 */
export interface ResourceReinstallReport {
  from: string;
  to: string;
  totals: ResourceReinstallReportTotals;
  days: ResourceReinstallReportDay[];
  topVms: ResourceReinstallReportVm[];
  topTenants: ResourceReinstallReportTenant[];
}

export interface ResourceFilters {
  search?: string;
  type?: ResourceType | "All";
  /**
   * "Active" (or undefined) lists live rows that are not Terminated; "All" lists every row, deleted
   * ones included (sent as `allStatuses=true`); a status is matched on every row.
   */
  status?: ResourceStatus | "All" | "Active";
  tenantId?: string;
  createdFrom?: string;
  createdTo?: string;
  /**
   * "Expiring within N days" window, counted from now. The backend accepts 1-365; the console
   * offers 3 and 7. Undefined means no expiry filter at all.
   */
  expiringInDays?: number;
  /**
   * Keeps only machines recorded on the product line with this slug ("cheap", "eco", "standard",
   * "performance"); undefined means no product-line filter.
   */
  productLineSlug?: string;
  page: number;
  pageSize: number;
}

// ============================================================================
// Audit Log Types
// ============================================================================

/**
 * One audit-log row as returned by GET /api/v1/admin/audit-logs. Mirrors the backend
 * `AuditLogListDto` (WiCloud.Admin.Application/Features/AuditLogs/DTOs/AuditLogListDto.cs)
 * field-for-field.
 *
 * `action`, `category`, `entityType` and `severity` are plain strings on purpose: there is no
 * backend enum behind them, every call site writes a free-form value, and the filter dropdowns are
 * populated from {@link AuditLogFacets} instead of a union declared here — a hand-maintained union
 * is what previously had the console offering "Create"/"Update"/"Delete", none of which any row has
 * ever been written with.
 */
export interface AuditLogListItem {
  id: string;
  /** Null for platform-level events, which includes everything done from the admin console. */
  tenantId?: string | null;
  tenantName?: string | null;
  userId?: string | null;
  /** The actor as recorded on the row itself — in practice their email address. */
  username?: string | null;
  /** Dotted lower-case action; an `admin.` prefix means it came from the admin console. */
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  category: string;
  /** info | warning | error */
  severity: string;
  description?: string | null;
  ipAddress?: string | null;
  occurredAt: string;
  createdAt: string;
}

/**
 * Full audit-log row from GET /api/v1/admin/audit-logs/{id}. Mirrors the backend
 * `AuditLogDetailDto`. The JSON columns arrive as strings — parse them with `parseAuditJson`.
 */
export interface AuditLogDetail extends AuditLogListItem {
  userAgent?: string | null;
  requestId?: string | null;
  /** Entity state before the change, as a JSON string. */
  stateBefore?: string | null;
  /** Entity state after the change, as a JSON string. */
  stateAfter?: string | null;
  /** Legacy combined before/after payload; null on every row written since the split. */
  changes?: string | null;
  /** Per-action metadata as a JSON string; "{}" when the action recorded none. */
  metadata: string;
}

/**
 * The values the filter dropdowns offer, from GET /api/v1/admin/audit-logs/filters.
 * Read from the rows that exist rather than declared here, so the list cannot mismatch from the data.
 */
export interface AuditLogFacets {
  actions: string[];
  /**
   * Every entity type the backend is able to write, unioned with what the data already holds —
   * so a type is offered before any row carries it, instead of quietly missing from the dropdown.
   */
  entityTypes: string[];
}

/**
 * Query string for the audit-log list and export.
 *
 * `entityId` is what the screen's search box fills when the operator pastes a GUID — the API binds
 * it as a GUID, so a non-GUID term must never reach it.
 */
export interface AuditLogListFilters {
  search?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  userId?: string;
  tenantId?: string;
  /** ISO 8601 with the browser's UTC offset — the backend expands it to the whole local day. */
  fromDate?: string;
  toDate?: string;
  page: number;
  pageSize: number;
}

// ============================================================================
// Dashboard Types (from wi-cloud-admin)
// ============================================================================

export interface DashboardMetrics {
  totalUsers: number;
  userChange: number; // percentage
  activeTenants: number;
  tenantChange: number; // percentage
  monthlyRevenue: number;
  revenueChange: number; // percentage
  totalResources: number;
  resourceChange: number; // percentage
}

export interface UserGrowthData {
  month: string;
  users: number;
  tenants: number;
}

export interface RevenueTrendData {
  month: string;
  revenue: number;
  target: number;
}

export interface ActivityItem {
  id: string;
  type: ActivityType;
  userId: string;
  userName: string;
  action: string;
  description: string;
  timestamp: string;
}

export interface TopResource {
  id: string;
  name: string;
  tenantName: string;
  type: string;
  usage: number; // percentage
  status: string;
}

export enum ActivityType {
  UserCreated = "UserCreated",
  UserSuspended = "UserSuspended",
  TenantCreated = "TenantCreated",
  TenantSuspended = "TenantSuspended",
  PaymentVerified = "PaymentVerified",
  PaymentRejected = "PaymentRejected",
  ResourceCreated = "ResourceCreated",
  ResourceDeleted = "ResourceDeleted",
}

export interface DashboardData {
  metrics: DashboardMetrics;
  userGrowth: UserGrowthData[];
  revenueTrend: RevenueTrendData[];
  recentActivities: ActivityItem[];
  topResources: TopResource[];
}

// ============================================================================
// Tenant Management Types (from wi-cloud-admin)
// ============================================================================

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string;
  ownerName: string;
  plan: string;
  status: TenantStatus;
  userCount: number;
  resourceCount: number;
  balance: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantDetail extends Tenant {
  users: TenantUser[];
  resources: TenantResource[];
  billing: TenantBilling;
  quotas: TenantQuotas;
  activities: TenantActivity[];
}

export interface TenantUser {
  userId: string;
  email: string;
  fullName: string;
  role: string;
  status: string;
  joinedAt: string;
}

export interface TenantResource {
  id: string;
  name: string;
  type: ResourceType;
  status: ResourceStatus;
  specs: Record<string, unknown>;
  createdAt: string;
}

export interface TenantBilling {
  balance: number;
  totalInvoices: number;
  totalPaid: number;
  totalPending: number;
  lastInvoiceDate: string | null;
  nextBillingDate: string | null;
}

export interface TenantQuotas {
  maxVMs: number;
  currentVMs: number;
  maxCPU: number;
  currentCPU: number;
  maxMemory: number; // GB
  currentMemory: number; // GB
  maxStorage: number; // GB
  currentStorage: number; // GB
  maxBandwidth: number; // GB
  currentBandwidth: number; // GB
}

export interface TenantActivity {
  id: string;
  userId: string;
  userName: string;
  action: string;
  description: string;
  createdAt: string;
}

export enum TenantStatus {
  Active = "Active",
  Suspended = "Suspended",
  Deleted = "Deleted",
}

export interface TenantFilters {
  search?: string;
  status?: TenantStatus;
  plan?: string;
  balanceFilter?: "positive" | "negative" | "all";
  pageNumber?: number;
  pageSize?: number;
}

export interface SuspendTenantRequest {
  tenantId: string;
  reason: string;
  notifyUsers: boolean;
}

export interface AdjustBalanceRequest {
  tenantId: string;
  amount: number;
  reason: string;
  type: "credit" | "debit";
}

export interface UpdateQuotasRequest {
  tenantId: string;
  quotas: Partial<TenantQuotas>;
}

// ============================================================================
// User Management Types (from wi-cloud-admin)
// ============================================================================

/**
 * User list item (matches UserListDto from backend)
 */
export interface User {
  id: string;
  email: string;
  fullName: string;
  role: string;
  phoneNumber: string | null;
  accountStatus: string; // Backend returns "Active", "Suspended", etc.
  createdAt: string;
  lastLoginAt: string | null;
  tenantCount: number;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  /** Where this person stands on each commitment, read from users.agreements. */
  agreements?: TenantAgreementStatusDto[];
}

/**
 * User detail (matches UserDetailDto from backend)
 */
export interface UserDetail {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber: string | null;
  company: string | null;
  country: string | null;
  accountStatus: string; // Backend returns string, not enum
  verificationLevel: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  twoFactorEnabled: boolean;
  createdAt: string;
  updatedAt: string | null;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  timezone: string;
  locale: string;
  currency: string;
  language: string;
  role: string;
  tenantCount: number;
  resourceCount: number;
  totalSpent: number;
  tenants?: UserTenant[];
  activityLogs?: UserActivity[]; // Deprecated on UserDetail: fetched via dedicated endpoints
  /**
   * Where this person stands on each commitment, read from users.agreements. The backend reuses
   * the tenant's status DTO for it.
   */
  agreements?: TenantAgreementStatusDto[];
}

/**
 * Tenant membership (matches TenantMembershipDto from backend)
 */
export interface UserTenant {
  tenantId: string;
  tenantName: string;
  tenantType: string;
  role: string;
  status: string;
  joinedAt: string;
  isDefault: boolean;
  tenantStatus: string;
}

/**
 * Activity log (matches AuditLogDto from backend)
 */
export interface UserActivity {
  id: string;
  userId?: string | null;
  username?: string | null;
  action?: string;
  actionType?: string; // Backend returns ActionType in AuditLogDto
  entityType?: string | null;
  entityId?: string | null;
  category?: string;
  severity?: string;
  description: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  metadata?: string | null;
  occurredAt?: string;
  createdAt: string;
}

export interface UserSession {
  id: string;
  ipAddress: string;
  userAgent: string;
  lastActivityAt: string;
  createdAt: string;
}

/**
 * Query string for a single user's activity log (GET /api/v1/admin/users/{id}/activity).
 *
 * Deliberately not called `AuditLogFilters`: it used to be, which made TypeScript merge it with the
 * list-screen interface of the same name into one type carrying both sets of fields.
 */
export interface UserActivityFilters {
  actionType?: string;
  dateFrom?: string; // ISO date string
  dateTo?: string; // ISO date string
  page: number;
  pageSize: number;
}

export type UserRole = AdminRole | "User";

export enum UserStatus {
  PendingVerification = "PendingVerification",
  Active = "Active",
  Suspended = "Suspended",
  Deleted = "Deleted",
}

export interface UserFilters {
  search?: string;
  status?: UserStatus | "All"; // Allow 'All' for filter UI
  registeredFrom?: string; // ISO date string
  registeredTo?: string; // ISO date string
  /** Commitment filter choice, e.g. "signup_commitment:accepted"; "all" or absent sends none. */
  agreement?: string;
  page: number;
  pageSize: number;
}

/**
 * Largest selection the export endpoint accepts in one request.
 *
 * Mirrors ExportUsersQueryValidator.MaxSelectedIds on the server. Enforced here too so hitting the
 * limit reads as a UI message instead of a raw validation error after the operator has already
 * ticked a thousand rows.
 */
export const MAX_EXPORT_SELECTION = 1000;

/** One row of the user Excel export, as returned by the export endpoint. */
export interface UserExportRow {
  fullName: string;
  phoneNumber?: string | null;
  email: string;
}

/**
 * What the customer has done with virtual machines. Mirrors the backend `UserVmFilter` enum
 * member names exactly — the wire carries these strings.
 */
export const USER_VM_FILTERS = ["HasVm", "NoVm", "ExpiredNotRenewed"] as const;

export type UserVmFilter = (typeof USER_VM_FILTERS)[number];

/**
 * Payload for the user Excel export.
 *
 * Two mutually exclusive modes, matching the backend: a non-empty `userIds` exports exactly those
 * rows and ignores every other field; an empty one falls back to the filters, where the
 * registration date range is mandatory.
 */
export interface ExportUsersRequest {
  userIds: string[];
  status?: UserStatus;
  /**
   * Virtual-machine filter, matching the backend `UserVmFilter` enum names. `undefined` exports
   * every user. Ignored when `userIds` is non-empty.
   */
  vmFilter?: UserVmFilter;
  registeredFrom?: string; // ISO date string
  registeredTo?: string; // ISO date string
}

export interface SuspendUserRequest {
  userId: string;
  reason: string;
  duration?: number; // days
}

export interface ImpersonateUserRequest {
  userId: string;
  reason: string;
}

// ============================================================================
// OS Image Management Types
// ============================================================================

/**
 * Catalog category stored on the row — this is what the admin form and the
 * customer-facing catalog group by.
 */
export type OSCategory = "Linux" | "Windows" | "BSD" | "Custom";

/**
 * OS family the backend derives from metadata.osFamily plus the display name.
 * Read-only: it is computed, never stored, so it can be filtered and shown but not edited.
 */
export type OSType =
  | "ubuntu"
  | "debian"
  | "rocky"
  | "alma"
  | "centos"
  | "fedora"
  | "windows"
  | "windows-server"
  | "freebsd"
  | "openbsd";

export interface OSImageSpecs {
  cpu: number;
  /** Memory in MB. */
  memory: number;
  /** Disk in GB. */
  disk: number;
}

/**
 * An OS image row as the admin API returns it (`OSImageAdminDto`).
 *
 * The three raw JSON strings are carried through verbatim so an edit can patch the fields
 * the form exposes and send everything else back untouched — the form renders a handful of
 * keys, the row holds many more.
 */
export interface AdminOSImage {
  id: string;
  name: string;
  version?: string;
  /** Derived by the backend; not editable. */
  osType?: OSType;
  category: string;
  description?: string;
  iconUrl?: string;
  /** WiService template name used at provisioning time (e.g. "Ubuntu-24.04"). */
  templateName?: string;
  minimumSpecs: OSImageSpecs;
  recommendedSpecs: OSImageSpecs;
  isActive: boolean;
  isFeatured: boolean;
  // sortOrder removed with the Sort Order field (catalog is ordered by name).
  usageCount: number;
  activeVMCount: number;
  defaultRegion?: string;
  currency: string;
  estimatedHourlyCost: number;
  estimatedMonthlyCost: number;
  tags: string[];
  /** Raw default_specifications JSON. */
  defaultSpecifications: string;
  /** Raw default_configuration JSON — the block provisioning reads. */
  defaultConfiguration: string;
  /** Raw metadata JSON. */
  metadata: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOSImageFilters {
  search?: string;
  category?: OSCategory | "All";
  osType?: OSType | "All";
  status?: "Active" | "Inactive" | "All";
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Flat shape the OS image form works with. The service converts it to and from the
 * backend contract, which stores most of these inside JSON columns.
 */
export interface OSImageFormValues {
  name: string;
  version: string;
  category: OSCategory;
  description: string;
  iconUrl?: string;
  templateName: string;
  minimumSpecs: OSImageSpecs;
  isActive: boolean;
  isFeatured: boolean;
  // sortOrder removed with the Sort Order field (catalog is ordered by name).
  /**
   * Windows images only — false when a VM built from this image cannot activate a licence.
   * Stored as `metadata.windowsLicense.activationSupported`.
   */
  licenseActivationSupported: boolean;
  /** Notice the customer VM wizard shows when licence activation is not supported. */
  licenseWarningMessage: string;
}

/** Outcome of a bulk activate / deactivate / delete call. */
export interface BulkOSImageResult {
  affectedCount: number;
  notFoundIds: string[];
  skippedIds: Record<string, string>;
  message: string;
}

// ============================================================================
// Product SKU Management Types
// ============================================================================

/**
 * Product SKU list item DTO
 *
 * Represents a product SKU in list view with basic information.
 */
export interface ProductSKUListDto {
  /** SKU ID (UUID) */
  id: string;

  /** Product ID (UUID) */
  productId: string;

  /** SKU name */
  name: string;

  /** SKU slug (URL-friendly identifier) */
  slug: string;

  /** SKU code (unique identifier) */
  skuCode: string;

  /** Whether SKU is active */
  isActive: boolean;

  /** Human-readable specifications summary */
  specificationsSummary: string;

  /** Number of pricing rules associated with this SKU */
  pricingRuleCount: number;

  /** Creation timestamp (ISO 8601) */
  createdAt: string;
}

/**
 * Product SKU detail DTO
 *
 * Complete product SKU information with specifications and pricing rules.
 */
export interface ProductSKUDetailDto {
  /** SKU ID (UUID) */
  id: string;

  /** Product ID (UUID) */
  productId: string;

  /** Product name */
  productName: string;

  /** SKU name */
  name: string;

  /** SKU slug (URL-friendly identifier) */
  slug: string;

  /** SKU description */
  description?: string;

  /** SKU code (unique identifier) */
  skuCode: string;

  /** Whether SKU is active */
  isActive: boolean;

  /** Specifications JSON object */
  specifications: Record<string, unknown>;

  /** Sort order for display */
  sortOrder: number;

  /** Metadata JSON string */
  metadata?: string;

  /** Associated pricing rules */
  pricingRules: PricingRuleDto[];

  /** Creation timestamp (ISO 8601) */
  createdAt: string;

  /** Last update timestamp (ISO 8601) */
  modifiedAt?: string;
}

/**
 * Create product SKU request DTO
 */
export interface CreateProductSKURequest {
  /** SKU name */
  name: string;

  /** SKU slug (URL-friendly identifier, optional - auto-generated if not provided) */
  slug?: string;

  /** SKU description */
  description?: string;

  /** SKU code (unique identifier) */
  skuCode: string;

  /** Specifications JSON object */
  specifications: Record<string, unknown>;

  /** Sort order (optional, default: 0) */
  sortOrder?: number;

  /** Metadata JSON string (optional) */
  metadata?: string;
}

/**
 * Update product SKU request DTO
 */
export interface UpdateProductSKURequest {
  /** SKU name */
  name: string;

  /** SKU description */
  description?: string;

  /** Whether SKU is active */
  isActive: boolean;

  /** Specifications JSON object */
  specifications: Record<string, unknown>;

  /** Sort order */
  sortOrder: number;

  /** Metadata JSON string */
  metadata?: string;
}

/**
 * Product SKU filters for list queries
 */
export interface ProductSKUFilters {
  /** Search term (searches name, skuCode, description) */
  search?: string;

  /** Active status filter */
  isActive?: boolean;

  /** Sort field */
  sortBy?: string;

  /** Sort order */
  sortOrder?: "asc" | "desc";

  /** Page number (1-based) */
  page?: number;

  /** Items per page */
  limit?: number;
}

// ============================================================================
// Resource Attachments Types
// ============================================================================

/**
 * Represents an attached resource (disk or network) to a VM
 */
export interface AttachedResourceDto {
  /** Attachment ID (needed for detachment operations) */
  attachmentId: string;
  /** Resource ID of the attached resource */
  resourceId: string;
  /** Name of the attached resource */
  resourceName: string;
  /** Device ID (e.g., "scsi0", "net0") */
  deviceId?: string;
  /** Timestamp when the resource was attached */
  attachedAt: string;

  // Disk-specific fields
  /** Disk size in GB (for disk attachments) */
  sizeGB?: number;
  /** IOPS (for disk attachments) */
  iops?: number;

  // Network-specific fields
  /** Bandwidth specification (for network attachments) */
  bandwidth?: string;
  /** VLAN ID (for network attachments) */
  vlanId?: number;
  /** IP address (for network attachments) */
  ipAddress?: string;
  /** MAC address (for network attachments) */
  macAddress?: string;
  /** Network switch/bridge (for network attachments) */
  switch?: string;
  /** Firewall enabled (for network attachments) */
  firewallEnabled?: boolean;
}

/**
 * Response containing all attached resources for a VM
 */
export interface ResourceAttachmentsResponse {
  /** List of attached disk resources */
  disks: AttachedResourceDto[];
  /** List of attached network resources */
  networks: AttachedResourceDto[];
}

// ============================================================================
// Resource Activity Log Types
// ============================================================================

/**
 * A single audit-log entry scoped to one resource, as returned by
 * GET /api/v1/admin/resources/{id}/activity-log. The endpoint returns the same
 * `AuditLogListDto` as the audit-log list screen, so it is the same type.
 */
export type ResourceActivityLogEntry = AuditLogListItem;

// ============================================================================
// Price Calculation Types
// ============================================================================

/**
 * Pricing tier information
 */
export interface PriceTierDto {
  /** Minimum quantity for this tier */
  minQuantity: number;

  /** Maximum quantity for this tier (undefined = unlimited) */
  maxQuantity?: number;

  /** Price per unit for this tier */
  pricePerUnit: number;

  /** Optional flat fee for this tier */
  flatFee?: number;
}

/**
 * Component breakdown in price calculation
 */
export interface ComponentBreakdownDto {
  /** Component code (e.g., "COMPUTE_CORE", "MEMORY_GB") */
  componentCode: string;

  /** Component display name */
  componentName: string;

  /** Quantity used */
  quantity: number;

  /** Unit price */
  unitPrice: number;

  /** Subtotal (quantity * unitPrice or tier calculation) */
  subtotal: number;

  /** Applied tier information (if tiered pricing) */
  tier?: PriceTierDto;
}

/**
 * Formula evaluation step
 */
export interface FormulaStepDto {
  /** Step number */
  step: number;

  /** Expression being evaluated */
  expression: string;

  /** Result of this step */
  result: number;
}

/**
 * Formula evaluation details
 */
export interface FormulaEvaluationDto {
  /** Original formula */
  formula: string;

  /** Evaluation steps */
  steps: FormulaStepDto[];

  /** Final result */
  finalResult: number;
}

/**
 * Price preview request
 */
export interface PricePreviewRequest {
  /** Product ID */
  productId: string;

  /** SKU ID (optional) */
  skuId?: string;

  /** Region code */
  region: string;

  /** Currency code */
  currency: string;

  /** Effective date (ISO 8601) */
  effectiveDate: string;

  /** Product specifications (JSON object) */
  specifications: Record<string, unknown>;
}

/**
 * Price preview response
 */
export interface PricePreviewResponse {
  /** Total calculated price */
  totalPrice: number;

  /** Currency code */
  currency: string;

  /** Component-level breakdown */
  breakdown: ComponentBreakdownDto[];

  /** Formula evaluation details (if formula-based) */
  formulaEvaluation?: FormulaEvaluationDto;
}

/**
 * Price comparison request
 */
export interface PriceComparisonRequest {
  /** Product ID */
  productId: string;

  /** SKU ID (optional) */
  skuId?: string;

  /** Region code */
  region: string;

  /** Currency code */
  currency: string;

  /** Effective date (ISO 8601) */
  effectiveDate: string;

  /** Comparison date (optional, ISO 8601) */
  comparisonDate?: string;

  /** Product specifications (JSON object) */
  specifications: Record<string, unknown>;
}

/**
 * Price comparison response
 */
export interface PriceComparisonResponse {
  /** Current pricing */
  current: PricePreviewResponse;

  /** Comparison pricing (if comparisonDate provided) */
  comparison?: PricePreviewResponse;

  /** Price difference */
  difference?: {
    /** Absolute difference */
    amount: number;

    /** Percentage difference */
    percentage: number;
  };
}

// ---------------------------------------------------------------------------
// VM recovery from infrastructure
//
// For a VM whose provisioning died half-way and which the infrastructure team
// then fixed by hand. WiCloud never learns that on its own: an Error resource
// is skipped by the status sync, so it stays broken for ever.
// ---------------------------------------------------------------------------

/** How far a recovery run got. Mirrors the backend `UpdateByUuidOutcome`. */
export const UPDATE_BY_UUID_OUTCOMES = [
  "Updated",
  "AlreadyUpToDate",
  "AppliedNotActivated",
  "ActivatedNotSettled",
] as const;
export type UpdateByUuidOutcome = (typeof UPDATE_BY_UUID_OUTCOMES)[number];

/**
 * One reason a recovery cannot proceed, or one thing to know before it does. Mirrors
 * `UpdateByUuidNoteDto`.
 *
 * The same shape carries both; which list it arrives in is the difference. A blocker stops the run,
 * a warning does not.
 */
export interface UpdateByUuidNote {
  code: string;
  message: string;
}

/** One disk as the infrastructure reports it. Mirrors `UpdateByUuidDiskDto`. */
export interface UpdateByUuidDisk {
  device: string;
  sizeGb: number;
  type: string | null;
  detached: boolean;
}

/** One network interface as the infrastructure reports it. Mirrors `UpdateByUuidNetworkDto`. */
export interface UpdateByUuidNetwork {
  device: string;
  /** Address with CIDR suffix, as the provider gives it. */
  ip: string | null;
  mac: string | null;
  switch: string | null;
  vlan: number | null;
}

/** The VM exactly as the infrastructure describes it. Mirrors `UpdateByUuidProviderStateDto`. */
export interface UpdateByUuidProviderState {
  vmUuid: string;
  cores: number;
  memoryMb: number;
  totalDiskGb: number;
  disks: UpdateByUuidDisk[];
  networks: UpdateByUuidNetwork[];
  /** CIDR stripped, ready to show. */
  primaryIpAddress: string | null;
  status: string | null;
  /** QEMU's own run state — the only field that reveals a paused VM. */
  qmpStatus: string | null;
}

/**
 * The answer to "is this VM really there, and what would recovering it do".
 * Mirrors `UpdateByUuidPreviewDto`. Read-only: nothing is written to produce it.
 */
export interface UpdateByUuidPreview {
  resourceId: string;
  resourceName: string;
  resourceStatus: string;
  /** What WiCloud has on record, or null when it has none. */
  recordedVmUuid: string | null;
  /**
   * The UUID actually checked: the recorded one, or — for a resource with none — the one the
   * operator entered. Null when there was nothing to check.
   */
  checkedVmUuid: string | null;
  vmExists: boolean;
  providerError: string | null;
  provider: UpdateByUuidProviderState | null;
  comparison: ResourceSpecMismatch | null;
  claimedByResourceId: string | null;
  claimedByResourceName: string | null;
  orderId: string | null;
  orderNumber: string | null;
  orderStatus: string | null;
  /** Shown for context only — recovery never moves it. */
  expiresAt: string | null;
  /** Status the resource would move to. Empty when the power state is not acted on. */
  targetStatus: string;
  blockers: UpdateByUuidNote[];
  /** Things to know that do not stop the run. */
  warnings: UpdateByUuidNote[];
  /** True when nothing stands in the way. Warnings do not affect it. */
  canUpdate: boolean;
  checkedAt: string;
  /**
   * Every resource row the run would touch — the VM first, then its disks and networks — each set
   * against the machine, matched to devices exactly as the write matches them.
   */
  rows: UpdateByUuidRow[];
  /**
   * The pool WiCloud records for the VM, or null. The infrastructure does not report one, so the
   * page offers `knownPoolNames` to choose from.
   */
  poolName: string | null;
  /** The pools the operator may choose ("dev" included), distinct and sorted. */
  knownPoolNames: string[];
  /** The catalogue template WiCloud records as the VM's OS, or null. */
  osTemplateId: string | null;
  /** The WiService template name WiCloud records as the VM's OS, or null. */
  osTemplateName: string | null;
  /** The operating systems the operator may choose; the infrastructure does not report an OS. */
  knownOperatingSystems: KnownOperatingSystem[];
}

/** One operating system the operator may choose. Mirrors `KnownOperatingSystem`. */
export interface KnownOperatingSystem {
  templateId: string;
  /** The display name ("Ubuntu 24.04"). */
  name: string;
  /** The WiService template name a reinstall sends ("ubuntu-24.04"). */
  templateName: string;
}

/** One resource row of the VM set against the machine. Mirrors `UpdateByUuidRowDto`. */
export interface UpdateByUuidRow {
  resourceId: string;
  name: string;
  /** "VirtualMachine", "Storage" or "Network". */
  type: ResourceType;
  /** The device the row is matched to ("scsi0", "net0"); null for the VM. */
  device: string | null;
  status: ResourceStatus;
  /** The status the run leaves the row in; equal to `status` when it does not move it. */
  statusAfter: ResourceStatus;
  /** Why the run leaves the VM's status as it is; VM row only, null when it moves it. */
  statusKeptReason: string | null;
  /**
   * False only for a child being deleted (Terminating, Terminated, Decommissioning), whose row the
   * run leaves alone.
   */
  rewritten: boolean;
  fields: UpdateByUuidField[];
}

/** One value as WiCloud holds it and as the infrastructure reports it. Mirrors `UpdateByUuidFieldDto`. */
export interface UpdateByUuidField {
  label: string;
  wiCloud: string | null;
  infrastructure: string | null;
  /**
   * Whether the run changes the value. With `infrastructure` null it is false (WiCloud's value is
   * kept) — except on a re-link, where a value naming the previous machine is erased.
   */
  differs: boolean;
}

/** One value copied from the infrastructure onto the record. Mirrors `UpdateByUuidChangeDto`. */
export interface UpdateByUuidChange {
  key: string;
  label: string;
  unit: string | null;
  from: number | null;
  to: number;
}

/**
 * Mirrors backend `ResourceConsoleDto`, the same shape the client app's `ResourceConsole` reads.
 * `consoleUrl` is single-use and short-lived: open it, never store it.
 */
export interface ResourceConsole {
  resourceId: string;
  consoleUrl: string;
  /** Seconds the link can still be opened. */
  linkExpiresInSeconds: number;
  /** Minutes the console session lasts once opened. */
  userExpiresInMinutes: number;
  generatedAt: string;
}

/**
 * Result of a VM password reset. `newPassword` is returned once, on this response only.
 */
export interface ResetVmPasswordResult {
  resourceId: string;
  username: string;
  newPassword: string;
  /** The running machine keeps the old password until it boots again. */
  requiresReboot: boolean;
}

/**
 * The power actions the `cloud-vm/state` webhook documents. Mirrors
 * `WiServiceVmActionCatalog.PowerActions` — the API refuses anything else.
 */
export const N8N_POWER_ACTIONS = [
  "start",
  "stop",
  "shutdown",
  "reboot",
  "suspend",
  "resume",
  "reset",
] as const;

export type N8nPowerAction = (typeof N8N_POWER_ACTIONS)[number];

/**
 * Result of one direct WiService (n8n) action on a VM. Mirrors `N8nVmActionDto`.
 */
export interface N8nVmActionResult {
  /** `power`, `specs`, `disk` or `delete`. */
  action: string;
  vmUuid: string;
  /** What was asked for, worded as it reads on the audit row. */
  summary: string;
  /** The provider's own message, passed through unchanged. */
  providerMessage: string;
  /** n8n execution id, for finding this run in the provider's own history. */
  executionId: number | null;
  /**
   * True when WiCloud's stored state now disagrees with the machine — a direct deletion, specs or disk
   * change, because this path writes nothing to the resource row. False for a power action: the
   * status sync re-reads power state on its own.
   */
  recordNeedsReconciling: boolean;
}

/** Body of `POST /api/v1/admin/resources/{id}/transfer-ownership`. */
export interface TransferResourceOwnershipPayload {
  /** The customer taking the machine over. Never the one that owns it today. */
  targetTenantId: string;
  /**
   * The user inside that customer who becomes the owner of record. Must be an active member of
   * `targetTenantId` — the backend refuses anyone else, because a resource whose user is not a
   * member of its tenant is invisible to everyone.
   */
  targetUserId: string;
  /** Why the machine changed hands. Recorded on both customers' audit rows. */
  reason: string;
}

/** Shortest transfer reason the backend accepts. */
export const TRANSFER_REASON_MIN_LENGTH = 5;

/** Longest transfer reason the backend accepts. */
export const TRANSFER_REASON_MAX_LENGTH = 500;

// ============================================================================
// Firewall Types
// ============================================================================

/**
 * A firewall rule read back from WiService. A blank optional field (undefined or "") means "all" -
 * any address, port or protocol, no macro; see `isAll` in `lib/firewallRules.ts`. Mirrors the
 * client console's `FirewallRule` (`wi-cloud-client/src/types/resource.ts`) - both consoles read
 * the same webhook shape, kept in step by hand since there is no shared package between them.
 */
export interface FirewallRule {
  pos?: number;
  type: "in" | "out";
  action: "ACCEPT" | "DROP" | "REJECT";
  enable: number;
  source?: string;
  sport?: string;
  dest?: string;
  dport?: string;
  iface?: string;
  proto?: string;
  log?: string;
  macro?: string;
  comment?: string;
  digest?: string;
}

/** Body of a firewall rule create, or the full rule shape an edit's PATCH body is built from. */
export interface CreateFirewallRuleRequest {
  type: "in" | "out";
  action: "ACCEPT" | "DROP" | "REJECT";
  enable: number;
  source?: string;
  sport?: string;
  dest?: string;
  dport?: string;
  iface?: string;
  proto?: string;
  log?: string;
  macro?: string;
  comment?: string;
}

/** A VM's global firewall policy. Every field is required on a write — see `updateFirewallOptions`. */
export interface FirewallOptions {
  enable: number;
  logLevelIn?: string;
  logLevelOut?: string;
  policyIn?: string;
  policyOut?: string;
  digest?: string;
}

/** Response of `GET .../firewall/rules`: the rule set and the options together. */
export interface FirewallRulesResponse {
  rules: FirewallRule[];
  options: FirewallOptions;
}

/** One line of `GET .../firewall/logs`. */
export interface FirewallLog {
  n: number;
  t: string;
}

/** One entry of `GET .../firewall/macros` - a named bundle of protocol/ports a rule can reference. */
export interface FirewallMacro {
  macro: string;
  desc: string;
}

/**
 * What a completed ownership transfer moved. Mirrors `TransferResourceOwnershipDto`.
 *
 * The resource id does not change — the same row changed owner, which is the whole point: the new
 * customer sees the machine in their own console and renews it from their own account.
 */
export interface TransferResourceOwnershipResult {
  resourceId: string;
  resourceName: string;
  /** The machine that changed hands. */
  vmUuid: string;
  previousTenantId: string;
  previousTenantName: string;
  newTenantId: string;
  newTenantName: string;
  newUserId: string;
  newUserEmail: string;
  /**
   * The paid-through date the new owner inherited, or null when there was no active billing cycle
   * to inherit — the new owner then has to renew before the machine keeps running.
   */
  inheritedUntil: string | null;
  /** The new owner's zero-cost cycle carrying `inheritedUntil`. Null when nothing was inherited. */
  newBillingCycleId: string | null;
  /** The previous owner's cycle the transfer closed. Null when there was none. */
  closedBillingCycleId: string | null;
  /**
   * The purchase order the resource was detached from. It stays with the customer who paid it —
   * only the pointer is cleared, and this id is kept here and in the audit row so the link is not
   * lost.
   */
  detachedOrderId: string | null;
  detachedOrderNumber: string | null;
  /**
   * False only when the resource had no stored login at all. The machine's password is unchanged
   * either way — which is why it must be reset straight afterwards.
   */
  /**
   * The previous owner's open renewal order, cancelled by the transfer. Null when they had none —
   * left alive it would be paid later and extend the NEW owner's machine on their money.
   */
  cancelledRenewalOrderNumber: string | null;
  /**
   * True when an open renewal order was left standing because it already holds a confirmed payment.
   * Not a failure: only a refund can undo money already taken, so it becomes an operator decision.
   */
  renewalOrderLeftForRefund: boolean;
  renewalOrderLeftForRefundNumber: string | null;
  credentialsMoved: boolean;
  childResourcesMoved: number;
  snapshotsMoved: number;
  firewallRulesMoved: number;
  /** The rate the resource carried in from the previous owner's purchase. */
  monthlyPriceBefore: number;
  /** The rate it carries now. Equal to `monthlyPriceBefore` when the catalogue could not price it. */
  monthlyPriceAfter: number;
  /**
   * False when the catalogue could not answer, so the figure the new owner sees still describes the
   * previous owner's purchase and their first renewal may come out at a different number.
   */
  monthlyPriceRefreshed: boolean;
  currency: string;
}
/**
 * What one recovery run did, end to end. Mirrors `UpdateByUuidExecutionDto`.
 *
 * A result comes back whenever anything committed, even when a later step stopped — the machine
 * really was recovered, so an error would say the opposite. `outcome` says how far it got and
 * `warning` what is left to do.
 */
export interface UpdateByUuidExecution {
  resourceId: string;
  vmUuid: string | null;
  /** The UUID the VM pointed at before, when the run re-linked it to a different machine. */
  relinkedFromVmUuid: string | null;
  /** Whether the run changed what it copies from the machine; the operator's choices are below. */
  recordChanged: boolean;
  /** The pool the operator set, when it changed the recorded one. */
  poolSetTo: string | null;
  /** The display name of the OS the operator set, when it changed the recorded one. */
  osSetTo: string | null;
  outcome: UpdateByUuidOutcome;
  applied: UpdateByUuidChange[];
  comparison: ResourceSpecMismatch | null;
  previousStatus: string;
  resourceStatus: string;
  activated: boolean;
  /** Disks and networks this run brought back to Running — also when the VM was already at its target. */
  childrenLifted: number;
  orderId: string | null;
  orderStatus: string | null;
  orderSettled: boolean;
  /** ORDER_NOT_SETTLEABLE | ORDER_REFUNDED | ORDER_NOT_PAID | ORDER_SIBLINGS_NOT_DELIVERED */
  orderBlockedReason: string | null;
  summary: string;
  warning: string | null;
}
